// Detalle de un ticket ciudadano — reutilizable desde CUALQUIER lugar donde
// aparezca un ticket (lista de buzones, reportes recientes, mapa, etc.).
// Muestra la conversación, la evidencia y el control de estado.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin, Phone, Image as ImageIcon, MessageSquare, CheckCircle2, Camera, Send,
  ArrowRightLeft, History, UploadCloud, Loader2, ScanSearch, ArrowRight, Lock, Undo2, Ban, Building2, Workflow,
} from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { AgentControl } from '@/components/AgentControl';
import { getThreadAgentState, sendHumanMessage, type ThreadDTO } from '@/lib/api';
import { useModules } from '@/context/ModulesContext';
import {
  attachEvidence, blockReporter, getBlocked, getConversation, getDependencies, listBuzones,
  unblockReporter, updateStatus, uploadReportEvidence,
} from '../api';
import type { CitizenReport, ConversationMsg } from '../types';
import { FLOW, NoteChip, STATUS_META, SectionLabel, StatusChip } from './parts';

// Re-export: otros puntos del módulo los importaban de aquí.
export { FLOW, PRIORITY_META, STATUS_META, StatusChip } from './parts';

// Formatea el timestamp ISO de la traza a fecha+hora legible (es-MX). El "cuándo"
// de la reasignación (auditable · gobierno). Fail-safe: devuelve el ISO crudo si no parsea.
function fmtTrace(iso: string): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Un turno de imagen llega como un blob interno del preprocesador (📄 DOCUMENTO …
// RESUMEN … [ASSETS] … [ARCHIVO GUARDADO] resource_id … [IMAGEN_ADJUNTA · URL…]).
// Lo convertimos en {imagen + descripción limpia}, ocultando rutas/resource_id/sig.
function parseVisionMessage(body: string): { imageUrl: string; caption: string } | null {
  if (!body || !/\[IMAGEN_ADJUNTA|📄\s*DOCUMENTO|\[ARCHIVO GUARDADO\]|\[ASSETS/i.test(body)) return null;
  const urlM = body.match(/\[IMAGEN_ADJUNTA[^\]]*URL p[uú]blica:\s*(https?:\/\/[^\s\]]+)/i);
  const imageUrl = urlM ? urlM[1].trim() : '';
  let caption = '';
  const rm = body.match(/RESUMEN:\s*([\s\S]*?)(?=\s*\[ASSETS)/i);
  if (rm) caption = rm[1];
  caption = caption
    .replace(/\[Descripci[oó]n visual[^\]]*\]:?/gi, '')
    .replace(/\bAn[aá]lisis de la imagen[^:]*:/gi, '')
    .replace(/\*\*/g, '')
    .replace(/`?photo_url`?:.*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { imageUrl, caption };
}

// Quita del texto las NOTAS INTERNAS del sistema (geocerca, instrucciones al modelo)
// que NUNCA vio el ciudadano y que confunden al director. Espeja
// services/output_hygiene.strip_internal_notes de Orión: la línea con "[NOTA INTERNA…"
// se corta entera; las oraciones "GEOCERCA:" se descartan. Puro; nunca lanza.
function stripInternalNotes(body: string): string {
  if (!body) return body;
  const kept: string[] = [];
  for (let line of body.split('\n')) {
    const ni = line.search(/\[?\s*NOTA INTERNA/i);
    if (ni >= 0) line = line.slice(0, ni);            // corta desde la nota hasta el fin de línea
    if (/GEOCERCA\s*:/i.test(line)) {
      line = line
        .split(/(?<=[.!?])\s+/)
        .filter((p) => p.trim() && !/GEOCERCA\s*:/i.test(p))
        .join(' ');
    }
    if (line.trim()) kept.push(line);
  }
  return kept.join('\n').trim();
}

// Un pin de WhatsApp llega como texto de andamiaje ("[Ubicación compartida … 📍 <dirección>
// (lat=…, lng=…) · mapa: https://…maps?q=…", o el formato viejo "q=<lat>,<lng>"). Lo
// convertimos en {dirección + coords + link a mapa} para pintarlo como una tarjeta de
// ubicación (como en WhatsApp) en vez de coordenadas crudas. Devuelve null si no es un pin.
function parseLocationMessage(body: string): {
  address: string; lat: string; lng: string; mapsUrl: string; approx: boolean;
} | null {
  if (!body) return null;
  const hasMarker = /\[Ubicaci[oó]n compartida|maps\?q=|geo:|\blat\s*=|(?:^|\s)q=-?\d/i.test(body);
  if (!hasMarker) return null;
  const m =
    body.match(/lat\s*=\s*(-?\d+(?:\.\d+)?)[,\s]+lng\s*=\s*(-?\d+(?:\.\d+)?)/i) ||
    body.match(/(?:maps\?q=|[?&]q=|geo:|\bq=)\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/i);
  if (!m) return null;
  const lat = m[1], lng = m[2];
  let address = '';
  const dm = body.match(/📍\s*([^\n(]+?)\s*(?:\(lat=|·|$)/);
  if (dm) address = dm[1].trim();
  if (!address) {
    const em = body.match(/·\s*etiqueta:\s*([^·\n]+)/i);
    if (em) address = em[1].trim();
  }
  return {
    address,
    lat, lng,
    mapsUrl: `https://www.google.com/maps?q=${lat},${lng}`,
    approx: /coordenadas APROXIMADAS/i.test(body),
  };
}

// Color del punto por tipo de evento de la bitácora.
const BITACORA_COLOR: Record<string, string> = {
  creado: '#38bdf8', estado: '#a78bfa', nota: '#64748b', reasignacion: '#f59e0b',
};

const CANAL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp', facebook: 'Messenger', instagram: 'Instagram', web: 'web',
  voice_phone: 'teléfono', voice_realtime: 'voz', email: 'correo', panel: 'panel',
};

export function TicketDetail({
  report, onClose, onChanged,
}: {
  report: CitizenReport;
  onClose: () => void;
  onChanged: (r: CitizenReport) => void;
}) {
  const [conv, setConv] = useState<ConversationMsg[]>([]);
  const { isAdmin, isDev, userRole } = useModules();
  const canModerate = isAdmin || isDev || userRole === 'admin';
  // La conversación se lee por TELÉFONO (puede abarcar varios reportes del mismo
  // ciudadano). El director no quiere leer desde el saludo inicial: lo llevamos al
  // momento en que se registró ESTE folio.
  const convRef = useRef<HTMLDivElement>(null);
  const anchorElRef = useRef<HTMLDivElement>(null);
  const [note, setNote] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Reasignación de dependencia (buzón). Se muestra en CUALQUIER lugar donde se
  // abra el ticket (este modal es el único).
  const [reassignTo, setReassignTo] = useState('');
  const [reason, setReason] = useState('');
  // Catálogo completo de dependencias (destinos) + tipo de reasignación.
  const [dependencies, setDependencies] = useState<string[]>([]);
  const [reassignType, setReassignType] = useState<'no_responsable' | 'depende'>('no_responsable');
  const [bitNote, setBitNote] = useState('');
  // Anti-spam: ¿el número del reportante está inhabilitado? (solo admin)
  const [blocked, setBlocked] = useState(false);
  // 🙋 Control humano (takeover). El hilo se resuelve por el TELÉFONO del reportante
  // (esta pantalla no tiene thread_id a la mano) y es la ÚNICA fuente de verdad: se
  // la pasamos a <AgentControl> como props en vez de que él la cargue por su cuenta,
  // porque el mismo estado decide si se puede escribir.
  const [thread, setThread] = useState<ThreadDTO | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);

  useEffect(() => {
    getConversation(report.folio).then(setConv).catch(() => setConv([]));
  }, [report.folio]);

  // ¿Cuál mensaje es "el momento del registro"? 1) el que confirma ESTE folio; si no
  // aparece (hilo de otro canal, folio con formato distinto), 2) el más cercano en el
  // tiempo a la creación del reporte; 3) el último. Ahí posicionamos el scroll.
  const anchorIdx = useMemo(() => {
    if (!conv.length) return -1;
    const folio = report.folio || '';
    if (folio) {
      for (let i = conv.length - 1; i >= 0; i--) {
        if ((conv[i].body || '').includes(folio)) return i;
      }
    }
    const created = report.created_at ? new Date(report.created_at).getTime() : NaN;
    if (!isNaN(created)) {
      let best = -1, bestDiff = Infinity;
      conv.forEach((m, i) => {
        const t = m.at ? new Date(m.at).getTime() : NaN;
        if (!isNaN(t)) {
          const d = Math.abs(t - created);
          if (d < bestDiff) { bestDiff = d; best = i; }
        }
      });
      if (best >= 0) return best;
    }
    return conv.length - 1;
  }, [conv, report.folio, report.created_at]);

  // Posiciona el scroll DENTRO del contenedor de la conversación (sin arrastrar el modal):
  // deja el ancla cerca del inicio para ver el registro y lo que siguió.
  useEffect(() => {
    const c = convRef.current;
    if (!c) return;
    const el = anchorElRef.current;
    if (!el) { c.scrollTop = c.scrollHeight; return; }
    const delta = el.getBoundingClientRect().top - c.getBoundingClientRect().top;
    c.scrollTop += delta - 8;
  }, [anchorIdx, conv]);

  useEffect(() => {
    const p = report.reporter_phone;
    // Moderación anti-spam: solo admin (el backend igual lo exige).
    if (!p || !canModerate) { setBlocked(false); return; }
    const tail = p.replace(/\D/g, '').slice(-10);
    getBlocked()
      .then((rows) => setBlocked(rows.some((b) => b.active && b.phone.replace(/\D/g, '').slice(-10) === tail)))
      .catch(() => {});
  }, [report.reporter_phone, canModerate]);

  useEffect(() => {
    const p = report.reporter_phone;
    if (!p) { setThread(null); return; }
    getThreadAgentState({ telefono: p }).then(setThread).catch(() => setThread(null));
  }, [report.reporter_phone]);

  // 🙋 Responder POR el ciudadano con el agente en pausa. Reusa el mismo envío que
  // las demás superficies (`sendHumanMessage`): el backend despacha al canal ACTIVO
  // de la org (Meta/Twilio, se resuelve solo) y persiste el turno como del ADMIN.
  const enviarRespuesta = async () => {
    const texto = reply.trim();
    if (!texto || sending) return;
    if (!thread?.id) { setSendErr('Aún no cargo la conversación de este número. Espera un momento.'); return; }
    setSending(true); setSendErr(null);
    try {
      await sendHumanMessage(thread.id, texto);
      setReply('');
      const fresco = await getConversation(report.folio).catch(() => null);
      if (fresco) setConv(fresco);
    } catch (e) {
      setSendErr((e as Error)?.message || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    // Buzones asignables (dependencia → volumen). El responsable sigue a la
    // dependencia vía la matriz, así que basta con elegir el buzón destino.
    // Catálogo de destinos; backends sin /dependencies → las dependencias con buzón.
    getDependencies()
      .then((d) => (Array.isArray(d) && d.length ? d : Promise.reject(new Error('vacío'))))
      .catch(() => listBuzones().then((bs) => bs.map((b) => b.dependency)))
      .then((d) => setDependencies(Array.from(new Set(d.filter(Boolean)))))
      .catch(() => setDependencies([]));
  }, []);

  // Forward-only: solo la SIGUIENTE fase (avance) + reabrir donde hay lógica.
  // El flujo NO retrocede — jamás vuelve a fases anteriores (única marcha atrás:
  // reabrir a 'en_proceso' desde atendido/cerrado). El backend valida lo mismo.
  type PhaseAction = { status: string; kind: 'advance' | 'close' | 'reopen' };
  const actions = useMemo<PhaseAction[]>(() => {
    const i = (FLOW as readonly string[]).indexOf(report.status);
    const out: PhaseAction[] = [];
    if (i !== -1 && i < FLOW.length - 1) {
      const next = FLOW[i + 1];
      out.push({ status: next, kind: next === 'cerrado' ? 'close' : 'advance' });
    }
    if (report.status === 'atendido' || report.status === 'cerrado') {
      out.push({ status: 'en_proceso', kind: 'reopen' });
    }
    return out;
  }, [report.status]);

  const move = async (status: string, kind: 'advance' | 'close' | 'reopen' = 'advance') => {
    if (kind === 'close' && !window.confirm(
      `¿Cerrar el ticket ${report.folio}?\n\nEsto CONCLUYE el ticket. Solo podrá reabrirse con permiso y motivo (queda en la bitácora).`
    )) return;
    if (kind === 'reopen' && !window.confirm(
      `¿Reabrir el ticket ${report.folio} a "En proceso"?\n\nQuedará registrado en la bitácora.`
    )) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await updateStatus({ folio: report.folio, status, note });
      setNote('');
      onChanged(updated);
      setMsg(`Movido a ${STATUS_META[status]?.label ?? status}.`);
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo actualizar.');
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    const dep = reassignTo.trim();
    if (!dep || dep === report.dependency) return;
    if (!window.confirm(
      `¿Reasignar el ticket ${report.folio}?\n\nDe "${report.dependency}" → "${dep}".\nEl folio NO cambia y queda registrado quién, cuándo y por qué (bitácora auditable).`
    )) return;
    setBusy(true); setMsg(null);
    try {
      // Reasignación pura: mismo estado, cambia la dependencia; el motivo (con el TIPO
      // al frente) va a la traza (dependency_history → bitácora). Backend valida scope.
      const typeLabel = reassignType === 'depende'
        ? 'Depende de otra instancia'
        : 'No es responsabilidad de esta dependencia';
      const note = reason.trim() ? `${typeLabel} · ${reason.trim()}` : typeLabel;
      const updated = await updateStatus({
        folio: report.folio, status: report.status, dependency: dep, note,
      });
      setReassignTo(''); setReason('');
      onChanged(updated);
      setMsg(`Reasignado a ${dep}.`);
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo reasignar.');
    } finally {
      setBusy(false);
    }
  };

  const addEvidence = async () => {
    if (!evidenceUrl.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await attachEvidence(report.folio, evidenceUrl.trim(), note);
      setEvidenceUrl('');
      onChanged(updated);
      setMsg('Evidencia adjuntada.');
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo adjuntar.');
    } finally {
      setBusy(false);
    }
  };

  // FASE 1 — Sube 1+ imágenes como ARCHIVO y las adjunta al reporte (se guardan en el
  // almacenamiento canónico; aparecen en la galería de evidencia). Se puede subir en
  // cualquier momento para actualizar la bitácora del reporte.
  const uploadEvidence = async (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];
    if (!files.length) return;
    setUploading(true); setMsg(null);
    try {
      const updated = await uploadReportEvidence(report.folio, files);
      onChanged(updated);
      setMsg(files.length === 1 ? 'Imagen subida y adjuntada.' : `${files.length} imágenes subidas y adjuntadas.`);
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo subir la evidencia.');
    } finally {
      setUploading(false);
    }
  };

  // Agrega una nota a la bitácora SIN cambiar el estado (mismo status + note → asiento).
  const addNote = async () => {
    if (!bitNote.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await updateStatus({ folio: report.folio, status: report.status, note: bitNote.trim() });
      setBitNote('');
      onChanged(updated);
      setMsg('Nota agregada a la bitácora.');
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo agregar la nota.');
    } finally {
      setBusy(false);
    }
  };

  // Anti-spam: inhabilita/habilita el número del reportante (solo admin). El
  // candado real vive en el backend; esto solo (des)marca el número.
  const toggleBlock = async () => {
    const phone = (report.reporter_phone || '').trim();
    if (!phone) return;
    setBusy(true); setMsg(null);
    try {
      if (!blocked) {
        const reason = window.prompt(
          `Inhabilitar ${phone} para crear reportes (anti-spam).\n\nMotivo (opcional):`, '',
        );
        if (reason === null) { setBusy(false); return; }  // cancelado
        await blockReporter(phone, reason || '');
        setBlocked(true);
        setMsg('Número inhabilitado: ya no podrá crear reportes.');
      } else {
        if (!window.confirm(`¿Habilitar de nuevo ${phone}? Volverá a poder crear reportes.`)) {
          setBusy(false); return;
        }
        await unblockReporter(phone);
        setBlocked(false);
        setMsg('Número habilitado.');
      }
    } catch (e: any) {
      setMsg(e?.message ?? 'No se pudo actualizar el bloqueo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open inShell onClose={onClose} label={`Reporte ${report.folio}`} title={
      <span className="flex items-center gap-2">
        <span className="font-mono">{report.folio}</span>
        <StatusChip status={report.status} />
        {report.needs_review && <NoteChip icon={ScanSearch}>Revisar</NoteChip>}
        {report.in_my_zone && (
          <NoteChip icon={MapPin} title={`Cae en tu zona · atiende ${report.dependency}`}>en tu zona</NoteChip>
        )}
      </span>
    }>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Columna izquierda: datos + conversación */}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="text-base font-semibold text-foreground">
              {report.category_label}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{report.description}</p>
            {report.needs_review && (
              <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                <span className="inline-flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-300"><ScanSearch className="h-3.5 w-3.5" aria-hidden /> Requiere revisión humana</span>
                {report.review_reason
                  ? ` — ${report.review_reason}`
                  : ' — la foto podría no corresponder al reporte; valídalo antes de atender.'}
              </div>
            )}
            <ul className="mt-3 flex flex-col gap-1.5 text-xs text-muted-foreground">
              <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /> {report.address}</li>
              {report.reporter_phone && (
                <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /> {report.reporter_name} · {report.reporter_phone}</li>
              )}
              <li className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /> {report.dependency}</li>
            </ul>
            {report.in_my_zone && (
              <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                <span className="font-semibold text-amber-700 dark:text-amber-300">En tu zona</span> — este reporte cae en tu polígono; lo atiende <span className="font-medium">{report.dependency}</span>. Puedes darle seguimiento en la bitácora.
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <SectionLabel icon={MessageSquare}>Conversación</SectionLabel>
              {report.reporter_phone && (
                <AgentControl
                  conv={{ telefono: report.reporter_phone }}
                  paused={thread ? !!thread.agent_paused : undefined}
                  pausedUntil={thread?.agent_paused_until ?? null}
                  indefinite={!!thread?.agent_paused_indefinite}
                  onChange={setThread}
                />
              )}
            </div>
            <div ref={convRef} className="max-h-64 space-y-2 overflow-y-auto rounded-xl bg-muted/60 p-3">
              {conv.map((m, i) => {
                const isAnchor = i === anchorIdx;
                const showDivider = isAnchor && anchorIdx > 0;
                // Un pin de ubicación y un turno de imagen solo vienen del ciudadano (inbound).
                const loc = m.direction === 'inbound' ? parseLocationMessage(m.body) : null;
                const vision = !loc && m.direction === 'inbound' ? parseVisionMessage(m.body) : null;
                const img = vision?.imageUrl || m.media_url;
                // Texto ya sin las notas internas del sistema (geocerca/instrucciones).
                const text = loc || vision ? '' : stripInternalNotes(m.body);
                // Si el mensaje era SOLO una nota interna, no queda nada que mostrar.
                if (!loc && !vision && !text && !m.media_url) return null;
                return (
                  <Fragment key={i}>
                    {showDivider && (
                      <div ref={anchorElRef} className="flex items-center gap-2 py-1 select-none">
                        <div className="h-px flex-1 bg-primary/30" />
                        <span className="text-[10px] uppercase tracking-wide font-medium text-primary/80">
                          Aquí se registró el reporte
                        </span>
                        <div className="h-px flex-1 bg-primary/30" />
                      </div>
                    )}
                    <div
                      data-msg-idx={i}
                      ref={isAnchor && !showDivider ? anchorElRef : undefined}
                      className={`flex ${m.direction === 'inbound' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${
                        m.direction === 'inbound'
                          ? 'bg-card text-foreground'
                          : 'bg-primary/15 text-foreground'
                      }`}>
                        {loc ? (
                          // 📍 Pin de WhatsApp como tarjeta de ubicación (no coordenadas crudas).
                          <a href={loc.mapsUrl} target="_blank" rel="noreferrer"
                             className="group flex items-center gap-2">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                              <MapPin className="h-5 w-5" />
                            </span>
                            <span className="min-w-0">
                              <span className="block font-medium text-foreground truncate group-hover:underline">
                                {loc.address || 'Ubicación compartida'}
                              </span>
                              <span className="block text-[11px] text-muted-foreground">
                                Ubicación compartida{loc.approx ? ' (aprox.)' : ''} · ver en el mapa
                              </span>
                            </span>
                          </a>
                        ) : vision ? (
                          <div className="space-y-1.5">
                            {img && (
                              <img src={img} alt="Foto del ciudadano" loading="lazy"
                                   className="rounded-md max-h-52 w-auto border border-border"
                                   onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            )}
                            {vision.caption && (
                              <div className="flex gap-1 text-xs italic text-muted-foreground">
                                <ScanSearch className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /><span>{vision.caption}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {text}
                            {m.media_url && (
                              <a href={m.media_url} target="_blank" rel="noreferrer"
                                 className="block text-xs text-primary mt-1 underline">ver adjunto</a>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </Fragment>
                );
              })}
              {!conv.length && (
                <div className="text-xs text-muted-foreground text-center py-4">
                  Sin conversación registrada para este folio.
                </div>
              )}
            </div>

            {/* Escribirle al ciudadano SIN salir del reporte. Solo con el control tomado:
                si el agente sigue activo, contestaría por su cuenta y se pisarían. */}
            {report.reporter_phone && (thread?.agent_paused ? (
              <div className="mt-2 space-y-1.5">
                <div className="flex gap-2 items-end">
                  <textarea
                    rows={2}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void enviarRespuesta(); }
                    }}
                    disabled={sending}
                    placeholder="Escríbele al ciudadano…"
                    className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => void enviarRespuesta()}
                    disabled={sending || !reply.trim()}
                    className="orion-btn-brand inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {sending
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Send className="h-3.5 w-3.5" />}
                    Enviar
                  </button>
                </div>
                {sendErr && <div className="text-xs text-destructive">{sendErr}</div>}
                <div className="text-[11px] text-muted-foreground">
                  Se envía por el canal de la conversación{thread?.channel ? ` (${CANAL_LABEL[thread.channel] ?? thread.channel})` : ''} y queda en el historial como respuesta de tu organización.
                </div>
              </div>
            ) : (
              <div className="mt-2 text-[11px] text-muted-foreground">
                Para escribirle al ciudadano, primero toma el control de la conversación con el botón de arriba.
                Mientras el agente esté activo, él sigue contestando.
              </div>
            ))}
          </div>

          {/* Bitácora: timeline de principio a fin (creación/estados/notas/reasignaciones) */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <SectionLabel icon={History}>Bitácora del reporte</SectionLabel>
            <ol className="mb-3 ml-1 space-y-2.5 border-l border-border pl-4">
              {(report.bitacora ?? []).map((e, i) => (
                <li key={i} className="relative text-xs">
                  <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-card"
                        style={{ backgroundColor: BITACORA_COLOR[e.kind] ?? '#64748b' }} />
                  <div className="text-foreground">{e.text}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {e.at ? fmtTrace(e.at) : ''}{e.by ? ` · ${e.by}` : ''}
                  </div>
                </li>
              ))}
              {!(report.bitacora ?? []).length && (
                <li className="text-xs text-muted-foreground list-none -ml-3">Sin movimientos aún.</li>
              )}
            </ol>
            <div className="flex gap-2">
              <input value={bitNote} onChange={(e) => setBitNote(e.target.value)}
                     placeholder="Agregar nota a la bitácora…" disabled={busy} aria-label="Nota para la bitácora"
                     className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
              <button type="button" onClick={addNote} disabled={busy || !bitNote.trim()}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-40">
                Agregar
              </button>
            </div>
          </div>
        </div>

        {/* Columna derecha: evidencia + control de estado */}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            {(() => {
              const stored = (report.evidence_images && report.evidence_images.length)
                ? report.evidence_images
                : (report.photo_url ? [{ url: report.photo_url, description: '' }] : []);
              // 🛟 Fallback UNIVERSAL: si el reporte no trae evidencia guardada (reporte
              // viejo, o cualquier ruta donde photo_url no se persistió), derívala de la
              // CONVERSACIÓN que ya cargamos — así NUNCA decimos "no adjuntó foto" si la
              // conversación demuestra que sí la envió.
              const convImgs: { url: string; description: string }[] = [];
              let convImageSeen = false;
              if (!stored.length) {
                for (const m of conv) {
                  if (m.direction !== 'inbound') continue;
                  const v = parseVisionMessage(m.body);
                  if (v) {
                    convImageSeen = true;
                    if (v.imageUrl) convImgs.push({ url: v.imageUrl, description: v.caption || '' });
                  } else if (m.media_url) {
                    convImageSeen = true;
                    convImgs.push({ url: m.media_url, description: '' });
                  }
                }
              }
              const imgs = stored.length ? stored : convImgs;
              const fromConv = !stored.length && imgs.length > 0;
              return (
                <>
                  <SectionLabel icon={ImageIcon}
                    extra={[imgs.length > 1 ? `· ${imgs.length} fotos` : '', fromConv ? '· de la conversación' : ''].filter(Boolean).join(' ') || undefined}>
                    Evidencia del ciudadano
                  </SectionLabel>
                  {imgs.length ? (
                    <div className="grid grid-cols-2 gap-2">
                      {imgs.map((im, i) => (
                        <a key={i} href={im.url} target="_blank" rel="noreferrer" className="block">
                          <img src={im.url} alt={`evidencia ${i + 1}`} loading="lazy"
                               className="h-28 w-full rounded-xl border border-border object-cover"
                               onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                          {im.description && (
                            <div className="mt-1 flex gap-1 text-[11px] leading-snug text-muted-foreground"><ScanSearch className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />{im.description}</div>
                          )}
                        </a>
                      ))}
                    </div>
                  ) : convImageSeen ? (
                    <div className="flex gap-1.5 text-xs text-muted-foreground">
                      <Camera className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> El ciudadano envió una imagen; la descripción está en la conversación (no se recuperó la URL de la foto).
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">El ciudadano no adjuntó foto.</div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <SectionLabel icon={UploadCloud}>Subir evidencia al reporte</SectionLabel>
            <label className={`inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-40 cursor-pointer focus-within:ring-2 focus-within:ring-primary/40 ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {uploading ? 'Subiendo…' : 'Elegir imagen(es)'}
              <input
                type="file" accept="image/*" multiple className="hidden" disabled={uploading}
                onChange={(e) => { void uploadEvidence(e.target.files); e.currentTarget.value = ''; }}
              />
            </label>
            <div className="text-[11px] text-muted-foreground mt-1">
              Una o varias imágenes · se guardan en el sistema y se adjuntan al reporte (aparecen en la galería de arriba).
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <SectionLabel icon={CheckCircle2} extra={`(${report.resolution_evidence.length})`}>Evidencia de resolución</SectionLabel>
            {report.resolution_evidence.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                {report.resolution_evidence.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer">
                    <img src={u} alt="evidencia arreglo"
                         className="h-16 w-16 rounded-xl border border-border object-cover" />
                  </a>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={evidenceUrl}
                onChange={(e) => setEvidenceUrl(e.target.value)}
                placeholder="URL de la foto del arreglo" aria-label="URL de la foto del arreglo"
                className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="button" onClick={addEvidence} disabled={busy || !evidenceUrl.trim()}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-40"
              >
                <Camera className="h-4 w-4" /> Adjuntar
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <SectionLabel icon={Workflow}>Avanzar el flujo</SectionLabel>
            <ol className="mb-3 flex items-center gap-1" aria-label="Fase actual">
              {FLOW.map((f, i) => {
                const cur = (FLOW as readonly string[]).indexOf(report.status);
                return (
                  <li key={f} className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="h-1.5 rounded-full" style={{ background: i <= cur ? STATUS_META[f].color : 'hsl(var(--muted))' }} />
                    <span className={`truncate text-[10px] ${i === cur ? 'font-semibold text-foreground' : 'text-muted-foreground'}`} aria-current={i === cur ? 'step' : undefined}>
                      {STATUS_META[f].label}
                    </span>
                  </li>
                );
              })}
            </ol>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota del avance / motivo (opcional)"
              rows={2} aria-label="Nota del avance"
              className="mb-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex flex-wrap gap-2">
              {actions.map((a) => {
                const meta = STATUS_META[a.status];
                const cls = a.kind === 'close'
                  ? 'border border-destructive/40 bg-card text-destructive hover:bg-destructive/10'
                  : a.kind === 'reopen'
                    ? 'border border-border bg-card text-foreground hover:bg-muted'
                    : 'orion-btn-brand bg-primary text-primary-foreground font-semibold';
                const Icon = a.kind === 'close' ? Lock : a.kind === 'reopen' ? Undo2 : ArrowRight;
                const label = a.kind === 'close' ? 'Cerrar ticket…'
                  : a.kind === 'reopen' ? 'Reabrir (En proceso)'
                  : `Avanzar a ${meta.label}`;
                return (
                  <button
                    type="button"
                    key={`${a.status}-${a.kind}`}
                    onClick={() => move(a.status, a.kind)}
                    disabled={busy}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40 ${cls}`}
                  >
                    {a.kind === 'reopen' && <Icon className="h-4 w-4" aria-hidden />}
                    {label}
                    {a.kind !== 'reopen' && <Icon className="h-4 w-4" aria-hidden />}
                  </button>
                );
              })}
              {!actions.length && (
                <span className="text-xs text-muted-foreground">Este ticket está en un estado terminal.</span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              El flujo avanza; no regresa a fases anteriores.
            </div>
          </div>

          {/* Reasignar a otra dependencia (buzón) — con traza auditable. */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-2">
            <SectionLabel icon={ArrowRightLeft}>Reasignar dependencia</SectionLabel>
            <div className="text-[11px] text-muted-foreground">
              Actual: <span className="font-medium text-foreground">{report.dependency || '—'}</span>. El responsable sigue a la dependencia; el folio no cambia.
            </div>
            <select
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              disabled={busy}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">Selecciona la dependencia destino…</option>
              {dependencies
                .filter((d) => d !== report.dependency)
                .map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
            </select>
            <select
              value={reassignType}
              onChange={(e) => setReassignType(e.target.value as 'no_responsable' | 'depende')}
              disabled={busy}
              title="Tipo de reasignación (queda en la bitácora)"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="no_responsable">No es responsabilidad de esta dependencia</option>
              <option value="depende">Depende de otra instancia</option>
            </select>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo/detalle (opcional, queda en la bitácora)"
              disabled={busy}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="button"
              onClick={reassign}
              disabled={busy || !reassignTo || reassignTo === report.dependency}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-40"
            >
              Reasignar
            </button>

            {!!report.dependency_history?.length && (
              <div className="pt-1">
                <div className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                  <History className="h-3 w-3" /> Historial de reasignación
                </div>
                <ul className="mt-1 space-y-1">
                  {report.dependency_history.map((d, i) => (
                    <li key={i} className="text-[11px] text-muted-foreground">
                      <span className="text-foreground">{d.from || '—'}</span> → <span className="text-foreground">{d.to}</span>
                      {d.reason ? ` · ${d.reason}` : ''}
                      <span className="opacity-70">{d.at ? ` · ${fmtTrace(d.at)}` : ''} · {d.by}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Moderación anti-spam: inhabilitar/habilitar el número (solo admin) */}
          {report.reporter_phone && canModerate && (
            <button
              type="button"
              onClick={toggleBlock}
              disabled={busy}
              title="Inhabilita este número para crear reportes. Reversible."
              className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-40 ${
                blocked
                  ? 'border-emerald-500/40 bg-card text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border-destructive/40 bg-card text-destructive hover:bg-destructive/10'
              }`}
            >
              {blocked
                ? <><CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Habilitar este número</>
                : <><Ban className="h-3.5 w-3.5" aria-hidden /> Inhabilitar este número (spam)</>}
            </button>
          )}

          {msg && (
            <div role="status" className="flex items-center gap-1.5 rounded-xl bg-muted/60 px-3 py-2 text-xs text-foreground">
              <Send className="h-3.5 w-3.5 text-primary" aria-hidden /> {msg}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}
